"use client"

import { startGoogleLogin } from "@lib/data/google-auth"
import { Button } from "@modules/common/components/ui"
import { useParams, useRouter } from "next/navigation"
import { useState } from "react"

const GoogleIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
)

function formatGoogleAuthError(message: string | undefined): string {
  if (!message) {
    return "Unable to start Google sign-in"
  }

  if (
    /Unable to retrieve the auth provider with id: google|au_google|GOOGLE_CLIENT/i.test(
      message
    )
  ) {
    return "Google sign-in is not configured yet. Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to the backend, then restart Medusa."
  }

  return message.replace(/^\s+/, "").trim()
}

const SocialLoginButtons = () => {
  const googleEnabled =
    process.env.NEXT_PUBLIC_GOOGLE_AUTH_ENABLED !== "false"

  const { countryCode } = useParams()
  const country = typeof countryCode === "string" ? countryCode : "th"
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!googleEnabled) {
    return null
  }

  const handleGoogle = async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await startGoogleLogin(country)

      if (result.location) {
        window.location.href = result.location
        return
      }

      if (result.loggedIn) {
        router.push(`/${country}/account`)
        return
      }

      setError(formatGoogleAuthError(result.error))
    } catch (err) {
      setError(
        formatGoogleAuthError(err instanceof Error ? err.message : String(err))
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full flex flex-col gap-y-3" data-testid="social-login">
      <Button
        type="button"
        variant="secondary"
        className="w-full"
        isLoading={loading}
        onClick={handleGoogle}
        data-testid="google-login-button"
      >
        <span className="inline-flex items-center justify-center gap-x-2">
          <GoogleIcon />
          Continue with Google
        </span>
      </Button>
      {error && (
        <p
          className="text-small-regular text-rose-500 text-center"
          data-testid="google-login-error"
        >
          {error}
        </p>
      )}
      <div className="relative flex items-center py-1">
        <div className="grow border-t border-gray-200" />
        <span className="mx-3 shrink text-small-regular text-ui-fg-muted uppercase">
          or
        </span>
        <div className="grow border-t border-gray-200" />
      </div>
    </div>
  )
}

export default SocialLoginButtons
